import React, { useCallback, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Modal from '~/components/Modal'
import { Controller, useForm } from 'react-hook-form'
import {
  useAppDispatch,
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import useGeocode from '@verzekeringapp-mono/shared/src/hooks/use_geocode'
import { updateFilters } from '@verzekeringapp-mono/shared/src/state/slices/companies_slice'
import useCompanies from '@verzekeringapp-mono/shared/src/hooks/use_companies'
import TextInput from '~/components/TextInput'
import Button from '~/components/Button'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'

interface IProps {
  onClose: () => void
  onLocationSelected?: (location: {
    latitude: string
    longitude: string
  }) => void
}

const selectClassName = 'w-full rounded-md border border-border-default bg-surface-input p-3 text-text-primary focus:border-border-focus focus:outline-none'

const LocationFilterModal = ({ onClose, onLocationSelected }: IProps) => {
  const filters = useAppSelector(state => state.companies.filters)
  const categoryOptions = useAppSelector(state => state.companies.categories)
  const radiusOptions = useAppSelector(state => state.companies.radius)
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const [citySelect, setCitySelect] = React.useState<
    {
      value: string
      text: string
    }[]
  >([])

  const { register, handleSubmit, watch, setValue, control, formState } =
    useForm({
      defaultValues: {
        location: filters.location || '',
        radius: filters.radius || 10,
        manufacturerType: filters.companyType || categoryOptions?.[0]?.id,
        brand: filters.brand,
        citySelect: ''
      }
    })

  const { searchRepairers } = useCompanies()

  const { reverseGeocodeAsyncGoogleMaps, geocodeAsyncGoogleMaps } = useGeocode()

  const dispatch = useAppDispatch()

  const getUserLocation = useCallback(async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async position => {
        const location = await reverseGeocodeAsyncGoogleMaps({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        })

        setValue('location', location?.postalCode || '', {
          shouldValidate: true
        })
      })
    }
  }, [reverseGeocodeAsyncGoogleMaps, setValue, navigator.geolocation])

  useEffect(() => {
    if (filters.location && filters.companyType && filters.radius || radiusOptions?.length === 0 || categoryOptions?.length === 0) {
      return
    }

    setValue('location', resourceAuthorization?.relationInfo?.zipCode || '')
    setValue('radius', radiusOptions?.[1] || 10)
    setValue('manufacturerType', categoryOptions?.[0]?.id || 0)

    handleSearchRepairers({
      location: resourceAuthorization?.relationInfo?.zipCode || '',
      radius: radiusOptions?.[1] || 10,
      manufacturerType: categoryOptions?.[0]?.id || 0,
      brand: categoryOptions?.[0]?.brands?.[0]?.id || null,
      citySelect: ''
    }, true)
  }, [radiusOptions, categoryOptions, resourceAuthorization])

  const selectedCategory = categoryOptions.find(
    category => watch('manufacturerType').toString() === category.id.toString()
  )

  const handleSearchRepairers = useCallback(
    async (handleData: {
      location: string
      radius: number
      manufacturerType: number
      brand: number | null
      citySelect: string
    }, disableCloseModal = false) => {
      const result = await searchRepairers({
        q: handleData.citySelect !== '' ? handleData.citySelect : handleData.location,
        radius: Number(handleData.radius),
        category_id: Number(handleData.manufacturerType),
        brand_id: Number(handleData.brand)
      })

      if (result && result.data?.citySelect?.length > 0) {
        setCitySelect(result.data.citySelect)
      } else {
        const latLng = await geocodeAsyncGoogleMaps(handleData.citySelect !== '' ? handleData.citySelect : handleData.location)

        if (onLocationSelected && latLng) {
          onLocationSelected({
            latitude: latLng.latitude.toString(),
            longitude: latLng.longitude.toString()
          })
        }

        dispatch(
          updateFilters({
            location: handleData.location,
            radius: Number(handleData.radius),
            companyType: handleData.manufacturerType,
            brand: handleData.brand
          })
        )

        if (!disableCloseModal) {
          onClose()
        }
      }
    },
    [searchRepairers, dispatch, updateFilters]
  )

  return (
    <Modal onClose={onClose}>
      <form
        onSubmit={handleSubmit(async data => {
          handleSearchRepairers(data)
        })}
      >
        <div className='mb-4'>
          <label
            htmlFor='location'
            className='mb-2 block font-semibold text-text-primary'
          >
            Locatie
          </label>

          <div
            className='relative flex items-center'
            style={{ height: '3.5rem' }}
          >
            <Controller
              control={control}
              name='location'
              rules={{ required: true }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  showValidationIcon={false}
                  shouldValidate={false}
                  id='location'
                  placeholder='Bijv. Oudkarspel of 1724 NT'
                  autoComplete='off'
                  data-lpignore='true'
                />
              )}
            />

            <FontAwesomeIcon
              icon={'location'}
              className='absolute right-4 cursor-pointer bg-surface-input text-icon-secondary'
              onClick={() => {
                getUserLocation()
              }}
            />
          </div>

          {citySelect.length > 0 && (
            <Controller
              control={control}
              name='citySelect'
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <div className='mt-2'>
                  <label
                    htmlFor='citySelect'
                    className='mb-2 block font-semibold text-text-primary'
                  >
                    Specificeer de locatie
                  </label>

                  <select
                    id='citySelect'
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    className={`${selectClassName} ${field.value ? 'text-text-primary' : 'text-text-placeholder'}`}
                  >
                    <option
                      value=''
                      disabled
                    >
                      Kies een stad…
                    </option>

                    {citySelect.map((city) => (
                      <option
                        key={city.value}
                        value={city.value}
                      >
                        {city.text}
                      </option>
                    ))}
                  </select>

                  {fieldState.error?.type === 'required' && (
                    <p className='mt-1 text-sm text-status-danger-foreground'>Locatie is verplicht.</p>
                  )}
                </div>
              )}
            />
          )}

        </div>

        <div className='mb-4'>
          <label
            htmlFor='radius'
            className='mb-2 block font-semibold text-text-primary'
          >
            Radius
          </label>

          <select
            id='radius'
            className={selectClassName}
            {...register('radius')}
          >
            {radiusOptions.map(radius => (
              <option
                key={radius}
                value={radius}
              >
                {radius} KM
              </option>
            ))}
          </select>
        </div>

        <div className='mb-4'>
          <label
            htmlFor='manufacturerType'
            className='mb-2 block font-semibold text-text-primary'
          >
            Type hersteller
          </label>

          <select
            id='manufacturerType'
            className={selectClassName}
            {...register('manufacturerType')}
          >
            {categoryOptions.map(category => (
              <option
                key={category.id}
                value={category.id}
              >
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCategory?.brands && selectedCategory.brands.length > 0 && (
          <div className='mb-4'>
            <label
              htmlFor='brand'
              className='mb-2 block font-semibold text-text-primary'
            >
              Merk
            </label>

            <select
              className={`${selectClassName} ${watch('brand') ? 'text-text-primary' : 'text-text-placeholder'}`}
              id='brand'
              {...register('brand')}
            >
              <option
                value=''
                disabled
                selected
                hidden
              >
                Selecteer merk
              </option>

              {selectedCategory.brands.map(brand => (
                <option
                  key={brand.id}
                  value={brand.id}
                >
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <Button
          type='submit'
          className='w-full'
          disabled={!formState.isValid}
        >
          Pas filters toe
        </Button>
      </form>
    </Modal>
  )
}

export default LocationFilterModal
